import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { EntityAction } from './types';

/**
 * Creates the Provider and hook for one entity's action set. The context
 * carries actions (methods that take the target), never entity data.
 */
export const createEntityActions = <
  TActions extends Record<string, EntityAction<never, never[]>>,
>({
  name,
  fallback,
}: {
  name: string;
  /** Used when no provider is mounted. Must hide every action. */
  fallback: () => TActions;
}) => {
  const Context = createContext<TActions | null>(null);
  Context.displayName = `${name}ActionsContext`;

  const Provider = ({
    actions,
    children,
  }: {
    actions: TActions;
    children: ReactNode;
  }) => <Context.Provider value={actions}>{children}</Context.Provider>;
  Provider.displayName = `${name}ActionsProvider`;

  const useActions = (): TActions => {
    const actions = useContext(Context);
    return useMemo(() => actions ?? fallback(), [actions]);
  };

  return { Provider, useActions, fallback };
};
